/**
 * REEDOS Knowledge Base — workflow page (`/kb/workflow/:slug`).
 *
 * Renders an end-to-end business workflow: prerequisites,
 * numbered steps (each with screens, approvals, outputs, audit
 * events), outputs, and audit implications.
 */
import { Link, useOutletContext, useParams } from "react-router-dom";
import { CheckCircle2, FileWarning, ClipboardList, ImageIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useKbWorkflow } from "@/lib/kb";

function StepBlock({ step, idx }) {
    return (
        <li
            className="border border-border rounded-md p-4"
            data-testid={`kb-workflow-step-${idx}`}
        >
            <div className="flex items-start gap-3">
                <div className="mt-1 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold shrink-0">
                    {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold" data-testid={`kb-workflow-step-title-${idx}`}>
                        {step.title}
                    </h3>
                    {(step.description_html || step.description) && (
                        step.description_html ? (
                            <div
                                className="kb-prose text-sm text-muted-foreground mt-1"
                                data-testid={`kb-workflow-step-desc-${idx}`}
                                dangerouslySetInnerHTML={{ __html: step.description_html }}
                            />
                        ) : (
                            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                                {step.description}
                            </p>
                        )
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                        {(step.screens || []).map((s) => (
                            <Badge key={`screen-${s}`} variant="outline" className="text-xs">
                                Screen: {s}
                            </Badge>
                        ))}
                        {(step.approvals || []).map((s) => (
                            <Badge key={`approval-${s}`} variant="secondary" className="text-xs">
                                Approval: {s}
                            </Badge>
                        ))}
                        {(step.outputs || []).map((s) => (
                            <Badge key={`output-${s}`} className="text-xs bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                                Output: {s}
                            </Badge>
                        ))}
                        {(step.audit_events || []).map((s) => (
                            <Badge key={`audit-${s}`} variant="outline" className="text-xs text-amber-800 border-amber-300">
                                Audit: {s}
                            </Badge>
                        ))}
                    </div>
                </div>
            </div>
        </li>
    );
}

export default function KBWorkflowPage() {
    const { "*": slug } = useParams();
    const { productCodeToName } = useOutletContext();
    const { data, loading, error } = useKbWorkflow(slug);

    if (loading) return <div className="p-4 text-muted-foreground">Loading…</div>;
    if (error?.response?.status === 403) {
        return (
            <div className="max-w-2xl border border-destructive/40 rounded-md p-6 bg-destructive/5" data-testid="kb-workflow-forbidden">
                <FileWarning className="h-6 w-6 text-destructive mb-2" />
                <h1 className="text-xl font-semibold">You don&apos;t have access to this workflow.</h1>
            </div>
        );
    }
    if (error?.response?.status === 404) {
        return (
            <div className="max-w-2xl border border-border rounded-md p-6 bg-muted/40" data-testid="kb-workflow-not-found">
                <FileWarning className="h-6 w-6 text-muted-foreground mb-2" />
                <h1 className="text-xl font-semibold">Workflow not found.</h1>
                <Link to="/kb" className="text-primary text-sm hover:underline mt-3 inline-block">
                    ← Back to the Knowledge Base
                </Link>
            </div>
        );
    }
    if (error) return <div className="p-4 text-destructive">Failed to load the workflow.</div>;

    const wf = data?.workflow;
    if (!wf) return null;

    return (
        <article className="max-w-3xl" data-testid="kb-workflow-page">
            <nav aria-label="Breadcrumb" className="mb-3 text-sm text-muted-foreground">
                <Link to="/kb" className="hover:text-foreground">Knowledge Base</Link>
                <span className="mx-1">›</span>
                <span>{productCodeToName?.[wf.product] || wf.product}</span>
                <span className="mx-1">›</span>
                <span>Workflows</span>
            </nav>

            <header className="mb-6">
                <div className="flex items-center gap-2">
                    <ClipboardList className="h-6 w-6 text-primary" />
                    <h1 className="text-3xl font-semibold" data-testid="kb-workflow-title">
                        {wf.title}
                    </h1>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                    {/* KB Phase A · A8 — maturity pill mirrors article page. */}
                    {wf.maturity && wf.maturity !== "published" && (
                        <Badge
                            variant="outline"
                            className="border-amber-300 text-amber-800"
                            data-testid={`kb-workflow-maturity-${wf.maturity}`}
                        >
                            {wf.maturity.toUpperCase()}
                        </Badge>
                    )}
                    {wf.version?.introduced && (
                        <Badge variant="secondary">App v{wf.version.introduced}</Badge>
                    )}
                    {wf.revision > 1 && (
                        <Badge variant="outline">rev {wf.revision}</Badge>
                    )}
                    {(wf.tags || []).map((t) => (
                        <Badge key={t} variant="outline">#{t}</Badge>
                    ))}
                </div>
            </header>

            {/* Prerequisites */}
            <section className="mb-6" data-testid="kb-workflow-prerequisites">
                <h2 className="text-xl font-semibold mb-2">Prerequisites</h2>
                <div
                    className="kb-prose"
                    dangerouslySetInnerHTML={{ __html: wf.prerequisites_html || "" }}
                />
            </section>

            {/* Diagram placeholder — KB Phase A · A8.
                Metadata-only in Phase A. We never render untrusted
                mermaid/plantuml/SVG source; the sanitised renderer
                lands in Phase B. */}
            {wf.diagram && (
                <section
                    className="mb-6 border border-dashed border-border rounded-md p-4 bg-muted/30"
                    data-testid="kb-workflow-diagram-placeholder"
                    aria-label="Workflow diagram (rendering deferred to Phase B)"
                >
                    <div className="flex items-start gap-3">
                        <ImageIcon className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <div className="text-sm font-semibold flex items-center gap-2">
                                Diagram
                                <Badge variant="outline" className="text-[10px] uppercase">
                                    {wf.diagram.type}
                                </Badge>
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                                {wf.diagram.alt}
                            </div>
                            <div className="text-xs text-muted-foreground mt-2">
                                Rendering deferred to Phase B — Phase A shows metadata only.
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Steps */}
            <section className="mb-6" data-testid="kb-workflow-steps">
                <h2 className="text-xl font-semibold mb-2">Steps</h2>
                <ol className="space-y-3">
                    {(wf.steps || []).map((s, i) => (
                        <StepBlock key={`step-${i}`} step={s} idx={i} />
                    ))}
                </ol>
            </section>

            {/* Outputs */}
            <section className="mb-6" data-testid="kb-workflow-outputs">
                <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    Outputs
                </h2>
                <div className="kb-prose" dangerouslySetInnerHTML={{ __html: wf.outputs_html || "" }} />
            </section>

            {/* Audit implications */}
            <section className="mb-6" data-testid="kb-workflow-audit">
                <h2 className="text-xl font-semibold mb-2">Audit Implications</h2>
                <div
                    className="kb-prose"
                    dangerouslySetInnerHTML={{ __html: wf.audit_implications_html || "" }}
                />
            </section>
        </article>
    );
}
